import React, { useEffect, useLayoutEffect, useState } from 'react';

import { css, keyframes, useTheme } from '@emotion/react';
import posthog from '@OnArk/utils/posthog';
import { createPortal } from 'react-dom';

import { TourStep } from './tours';

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(123, 44, 255, 0.55), 0 0 0 0 rgba(123, 44, 255, 0.25); }
  70%  { box-shadow: 0 0 0 6px rgba(123, 44, 255, 0), 0 0 0 16px rgba(123, 44, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(123, 44, 255, 0), 0 0 0 0 rgba(123, 44, 255, 0); }
`;

const fade = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function measure(selector: string): Rect | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector(selector);

  if (!el) return null;
  const r = el.getBoundingClientRect();

  return { height: r.height, left: r.left, top: r.top, width: r.width };
}

function scrollIntoViewIfNeeded(selector: string) {
  const el = document.querySelector(selector);

  if (!el) return;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const offscreen =
    r.bottom < 80 || r.top > vh - 80 || r.right < 0 || r.left > vw;

  if (offscreen) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

interface MicroTourProps {
  steps: TourStep[];
  // Triggered when the user finishes (last "Next") or skips.
  onClose: () => void;
}

const MicroTour: React.FC<MicroTourProps> = ({ onClose, steps }) => {
  const theme = useTheme();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [mounted, setMounted] = useState(false);
  // Filter the tour to hops whose anchor is actually in the DOM. We poll
  // briefly because some pages mount their content after a data fetch
  // (e.g. an empty-state vs full list) so we don't want to drop hops that
  // are about to appear. After the poll window, hops with no anchor are
  // dropped entirely — they were going to render a popover with no
  // spotlight ring, which makes the tour feel broken ("Step 2 of 4 — Find
  // a class later" when you have zero classes is just confusing).
  const [appliedSteps, setAppliedSteps] = useState<TourStep[]>([]);
  const step = appliedSteps[idx];

  // Portal needs document to exist
  useEffect(() => {
    setMounted(true);
  }, []);

  // Upfront-filter: try each selector now, then again at 200ms and 600ms.
  // Use the latest non-empty result as the applied step list. If nothing
  // resolves by 600ms, close silently — there's nothing useful to show.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    // Only drop hops whose anchor isn't in the DOM at all — don't try to
    // judge visibility here. Whether the rect has area is the spotlight
    // ring's problem, and it polls. Filtering by size caused trouble:
    // anchors that genuinely existed but were 0×0 for a frame (mid-
    // transition, behind a still-loading data fetch) were prematurely
    // dropped and the tour closed silently, which then burned the
    // tour-seen flag for the whole session.
    const tryFilter = () =>
      steps.filter(s => !!document.querySelector(s.selector));
    let settled = false;
    const settle = (filtered: TourStep[]) => {
      if (settled) return;
      settled = true;
      if (filtered.length === 0) {
        onClose();

        return;
      }
      setAppliedSteps(filtered);
    };
    // Cheap first pass: if every hop resolves immediately, no need to wait.
    const first = tryFilter();

    if (first.length === steps.length) {
      setAppliedSteps(first);

      return;
    }
    const t1 = window.setTimeout(() => {
      const r = tryFilter();

      if (r.length === steps.length) settle(r);
    }, 200);
    const t2 = window.setTimeout(() => settle(tryFilter()), 600);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      settled = true;
    };
  }, [steps]);

  // Re-measure target whenever the step changes, the window resizes, or the
  // page scrolls. ResizeObserver covers DOM changes inside the target.
  useLayoutEffect(() => {
    if (!step) return;
    let raf = 0;
    const update = () => {
      // Use rAF so we don't thrash layout on rapid scroll.
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setRect(measure(step.selector)));
    };

    scrollIntoViewIfNeeded(step.selector);
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    // Poll for late-mounted elements (some sections render after data fetch).
    const interval = window.setInterval(update, 400);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      window.clearInterval(interval);
    };
  }, [step?.selector, idx]);

  if (!mounted || !step) return null;

  const isFirst = idx === 0;
  const isLast = idx === appliedSteps.length - 1;

  const close = (reason: 'completed' | 'skipped') => {
    posthog?.capture('microTourClosed', { reason, stepIdx: idx });
    onClose();
  };

  const next = () => {
    if (isLast) close('completed');
    else setIdx(i => i + 1);
  };
  const prev = () => setIdx(i => Math.max(0, i - 1));

  // Compute callout position. Default: below the target. If target is near the
  // bottom of the viewport, render above. If target is too wide / off-screen,
  // pin to top center.
  const calloutWidth = 320;
  const margin = 12;
  let calloutTop = 24;
  let calloutLeft = 24;

  if (rect) {
    const vh = window.innerHeight;
    const placeBelow = rect.top + rect.height + margin + 240 < vh;

    calloutTop = placeBelow
      ? rect.top + rect.height + margin
      : Math.max(margin, rect.top - margin - 220);
    calloutLeft = Math.min(
      Math.max(margin, rect.left + rect.width / 2 - calloutWidth / 2),
      window.innerWidth - calloutWidth - margin
    );
  }

  return createPortal(
    <>
      {/* Spotlight ring tracking the target. Pointer-events:none so the user
          can still interact with the highlighted element. */}
      {rect && (
        <div
          aria-hidden
          css={css`
            position: fixed;
            top: ${rect.top - 4}px;
            left: ${rect.left - 4}px;
            width: ${rect.width + 8}px;
            height: ${rect.height + 8}px;
            border: 2px solid ${theme.colors.clickable};
            border-radius: 8px;
            pointer-events: none;
            z-index: 10;
            animation: ${pulse} 1.4s ease-out infinite;
            transition:
              top 0.18s ease,
              left 0.18s ease,
              width 0.18s ease,
              height 0.18s ease;
          `}
        />
      )}
      {/* Step callout */}
      <div
        aria-label={`Tour step ${idx + 1} of ${appliedSteps.length}`}
        css={css`
          position: fixed;
          top: ${calloutTop}px;
          left: ${calloutLeft}px;
          width: ${calloutWidth}px;
          background: ${theme.colors.white};
          border: 1px solid ${theme.colors.clickableLight};
          border-radius: ${theme.radius.md};
          box-shadow: ${theme.shadows.lg};
          padding: 1.2rem 1.4rem;
          z-index: 11;
          animation: ${fade} 0.18s ease;
        `}
        role="dialog"
      >
        <div
          css={{
            color: theme.colors.clickable,
            fontSize: '1rem',
            fontWeight: 600,
            letterSpacing: '1px',
            marginBottom: '0.4rem',
            textTransform: 'uppercase',
          }}
        >
          Step {idx + 1} of {appliedSteps.length}
        </div>
        <h3
          css={{
            color: theme.colors.black,
            fontSize: '1.5rem',
            fontWeight: 500,
            margin: '0 0 0.5rem',
          }}
        >
          {step.title}
        </h3>
        <p
          css={{
            color: theme.colors.blackLight,
            fontSize: '1.3rem',
            lineHeight: 1.45,
            margin: 0,
          }}
        >
          {step.body}
        </p>
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1.2rem',
          }}
        >
          <button
            css={{
              background: 'transparent',
              border: 0,
              color: theme.colors.grayDark,
              cursor: 'pointer',
              fontSize: '1.2rem',
              padding: '0.4rem 0.6rem',
            }}
            type="button"
            onClick={() => close('skipped')}
          >
            Skip
          </button>
          <div css={{ display: 'flex', gap: '0.6rem' }}>
            <button
              css={{
                background: 'transparent',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.radius.md,
                color: theme.colors.black,
                cursor: isFirst ? 'not-allowed' : 'pointer',
                fontSize: '1.2rem',
                opacity: isFirst ? 0.4 : 1,
                padding: '0.5rem 1rem',
              }}
              disabled={isFirst}
              type="button"
              onClick={prev}
            >
              ← Back
            </button>
            <button
              css={{
                background: theme.colors.clickable,
                border: 0,
                borderRadius: theme.radius.md,
                color: theme.colors.white,
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: 500,
                padding: '0.5rem 1rem',
              }}
              type="button"
              onClick={next}
            >
              {isLast ? 'Done' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default MicroTour;
